/**
 * Data Deletion Status Page
 *
 * Public page required by Meta App Review.
 * Backend returns this URL in the data-deletion callback response:
 *   ${CLIENT_URL}/data-deletion-status?code=${confirmationCode}
 *
 * Displays confirmation that a data deletion request has been received.
 */

import { Link, useSearchParams } from 'react-router-dom';
import { t } from '@/i18n';
import LegalPageLayout from './LegalPageLayout';

const TOC_SECTIONS = [
  { id: 'status', title: 'Deletion Status' },
  { id: 'what-next', title: 'What Happens Next' },
  { id: 'oauth-revoke', title: 'OAuth Token Revocation' },
  { id: 'contact', title: 'Contact Us' },
];

export default function DataDeletionStatus() {
  const [searchParams] = useSearchParams();
  const confirmationCode = searchParams.get('code') || 'N/A';

  return (
    <LegalPageLayout
      title="Data Deletion Request"
      subtitle="Your request to delete your data has been received."
      effectiveDate="February 25, 2026"
      lastUpdated="February 25, 2026"
      sections={TOC_SECTIONS}
    >
      {/* Deletion Status */}
      <section id="status" className="scroll-mt-32">
        <h2 className="text-2xl text-gray-900 font-semibold mb-6">Deletion Status</h2>

        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="text-green-800 font-semibold text-lg">Request Received</p>
              <p className="text-green-700 mt-1">
                Your data deletion request has been successfully submitted and is being processed.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <p className="text-gray-600 mb-2">
            <strong className="text-gray-900">Confirmation Code:</strong>
          </p>
          <p className="font-mono text-lg text-gray-900 bg-white border border-gray-200 rounded px-4 py-2 inline-block">
            {confirmationCode}
          </p>
          <p className="text-gray-500 text-sm mt-3">
            Please save this confirmation code for your records. You may need it to check the status of your request.
          </p>
        </div>
      </section>

      {/* What Happens Next */}
      <section id="what-next" className="scroll-mt-32">
        <h2 className="text-2xl text-gray-900 font-semibold mb-6">What Happens Next</h2>
        <ul className="list-disc ml-6 space-y-3 text-gray-600">
          <li>
            <strong className="text-gray-900">Processing Time:</strong> Your data deletion request will be processed within 30 days in accordance with GDPR and applicable data protection regulations.
          </li>
          <li>
            <strong className="text-gray-900">Scope:</strong> All personal data associated with your Facebook or Instagram account connection to BuzzBit will be permanently deleted.
          </li>
          <li>
            <strong className="text-gray-900">Exceptions:</strong> Certain data may be retained where required by law (e.g., billing records for tax compliance) as outlined in our{' '}
            <Link to="/privacy" className="text-teal-400 hover:text-teal-300 underline">{t('landing.footer.privacy')}</Link>
            {' '}and{' '}
            <Link to="/cookies" className="text-teal-400 hover:text-teal-300 underline">{t('landing.footer.cookies')}</Link>.
          </li>
          <li>
            <strong className="text-gray-900">Confirmation:</strong> You will not receive additional confirmation once the deletion is complete. The absence of your data from our systems serves as confirmation.
          </li>
        </ul>
      </section>

      {/* OAuth Token Revocation */}
      <section id="oauth-revoke" className="scroll-mt-32">
        <h2 className="text-2xl text-gray-900 font-semibold mb-6">OAuth Token Revocation</h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          If you arrived here after revoking access for a third-party application connected to BuzzBit X via OAuth 2.0 (e.g. Claude Desktop, Claude Code, or your own integration), here&apos;s exactly what happens:
        </p>
        <ul className="list-disc ml-6 space-y-3 text-gray-600 mb-4">
          <li>
            <strong className="text-gray-900">Immediate:</strong> Your access token and refresh token are invalidated. The third-party application can no longer call any MCP tool with the revoked tokens — subsequent requests to <code className="px-1 bg-gray-100 rounded">api.buzzbitx.com/mcp</code> return HTTP 401.
          </li>
          <li>
            <strong className="text-gray-900">Audit log:</strong> A record of the revocation (timestamp, token ID, reason) is added to your workspace audit log and retained for 90 days.
          </li>
          <li>
            <strong className="text-gray-900">Data already retrieved:</strong> The third-party application may have local copies of data it retrieved before the revocation. BuzzBit X has no control over data once it has been returned to the connected application. To request deletion of data held by a third party, contact that application directly.
          </li>
          <li>
            <strong className="text-gray-900">For Anthropic Claude:</strong> see Anthropic&apos;s data deletion procedure at{' '}
            <a href="https://anthropic.com/privacy" target="_blank" rel="noopener noreferrer" className="text-teal-400 hover:text-teal-300 underline">
              anthropic.com/privacy
            </a>.
          </li>
          <li>
            <strong className="text-gray-900">No BuzzBit X data is deleted by token revocation.</strong> Your campaigns, customers, products, and other workspace data remain in BuzzBit X. To delete BuzzBit X data, use the workspace data-deletion procedure described in our{' '}
            <Link to="/privacy" className="text-teal-400 hover:text-teal-300 underline">{t('landing.footer.privacy')} §5</Link>.
          </li>
        </ul>
        <p className="text-gray-600 leading-relaxed">
          To revoke an OAuth token at any time, sign in to BuzzBit X and go to{' '}
          <strong className="text-gray-900">Settings → Integrations → API Keys</strong>, or call{' '}
          <code className="px-1 bg-gray-100 rounded">POST /oauth/revoke</code> (RFC 7009) with the token in the request body.
        </p>
      </section>

      {/* Contact Us */}
      <section id="contact" className="scroll-mt-32">
        <h2 className="text-2xl text-gray-900 font-semibold mb-6">Contact Us</h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          If you have questions about your data deletion request, please contact us:
        </p>
        <div className="bg-gray-50/50 border border-gray-200 rounded-lg p-6 space-y-3">
          <p className="text-gray-600">
            <strong className="text-gray-900">Privacy Inquiries:</strong>{' '}
            <a href="mailto:support@buzzbitx.com" className="text-teal-400 hover:text-teal-300 underline">
              support@buzzbitx.com
            </a>
          </p>
          <p className="text-gray-600">
            <strong className="text-gray-900">Data Protection Officer:</strong>{' '}
            <a href="mailto:support@buzzbitx.com" className="text-teal-400 hover:text-teal-300 underline">
              support@buzzbitx.com
            </a>
          </p>
        </div>
      </section>
    </LegalPageLayout>
  );
}
